import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AppShell } from '@/shared/ui/AppShell'
import { Skeleton } from '@/shared/ui/Skeleton'
import { ErrorState } from '@/shared/ui/States'
import { useTopicTrendData } from '../state/useTopicTrendData'
import { DateHistogram } from './DateHistogram'
import type { TabKind } from '../model/contracts'

export function TopicTrendPage() {
  const { topicId = '' } = useParams<{ topicId: string }>()
  const { t } = useTranslation('topicTrend')
  const [tab, setTab] = useState<TabKind>('posts')

  const { data, isLoading, error } = useTopicTrendData(topicId)

  const tabs: { key: TabKind; label: string }[] = [
    { key: 'posts', label: t('tab.posts') },
    { key: 'news', label: t('tab.news') },
  ]

  const filteredSources = data?.sources.filter((s) =>
    tab === 'news' ? s.kind === 'news' : s.kind === 'fb',
  )

  return (
    <AppShell showAnalytics={false}>
      <div className="mx-auto w-full max-w-2xl px-4 py-6">
        <div className="mb-6 flex items-center gap-2">
          <TrendingUp size={20} className="text-primary" />
          <h1 className="text-xl font-semibold text-foreground">
            {data?.topicName ? `${data.topicName} — ${t('title')}` : t('title')}
          </h1>
        </div>

        {isLoading && (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-24 w-full rounded-xl" />
          </div>
        )}

        {error != null && !isLoading && <ErrorState error={error} />}

        {data && !isLoading && (
          <div className="flex flex-col gap-5">
            <div className="flex gap-1 rounded-full bg-secondary/30 p-0.5 ring-1 ring-primary/15 w-fit">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={cn(
                    'min-h-[36px] rounded-full px-4 py-1.5 text-xs font-semibold transition-all active:scale-95',
                    tab === item.key
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <DateHistogram days={data.days} tab={tab} />

            <section className="rounded-xl border border-border bg-card p-4">
              <h2 className="mb-3 text-sm font-semibold text-foreground">
                {t('perSource')}
              </h2>
              {filteredSources && filteredSources.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {filteredSources.map((src) => (
                    <span
                      key={src.domain}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-3 py-1 text-xs font-medium text-foreground"
                    >
                      {src.domain}
                      <span className="text-muted-foreground">({src.postCount})</span>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">{t('empty')}</p>
              )}
            </section>
          </div>
        )}
      </div>
    </AppShell>
  )
}
